import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Box, Alert, CircularProgress, Tooltip,
  Collapse, Grid, Card, CardContent, CardActions, Tabs, Tab, Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UndoIcon from '@mui/icons-material/Undo';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArticleIcon from '@mui/icons-material/Article';
import ScaleIcon from '@mui/icons-material/Height';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import TuneIcon from '@mui/icons-material/TuneRounded';
import { swarmApi } from '../api';

const emptyDeployForm = () => ({
  name: '',
  image: '',
  replicas: 1,
  publishedPort: '',
  targetPort: '',
  envText: '',
  network: '',
});

function Services() {
  const [services, setServices] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [workloads, setWorkloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState(0);
  const [expandedService, setExpandedService] = useState(null);
  const [serviceContainers, setServiceContainers] = useState({});

  // Dialogs
  const [logsDialog, setLogsDialog] = useState({ open: false, id: '', name: '', logs: '', loading: false });
  const [detailDialog, setDetailDialog] = useState({ open: false, data: null, title: '' });
  const [volumeDialog, setVolumeDialog] = useState({ open: false, name: '', driver: 'local' });
  const [orchestratorDialog, setOrchestratorDialog] = useState({ open: false, serviceId: '', constraints: '', memoryLimit: '' });
  const [deployDialog, setDeployDialog] = useState({ open: false, ...emptyDeployForm(), submitting: false });
  const [hpaDialog, setHpaDialog] = useState({ open: false, workload: null, submitting: false });
  const [explainDialog, setExplainDialog] = useState({ open: false, title: '', text: '', loading: false });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sRes, vRes, wRes] = await Promise.all([
        swarmApi.listServices().catch(() => ({ data: [] })),
        swarmApi.listVolumes().catch(() => ({ data: [] })),
        swarmApi.listWorkloads().catch(() => ({ data: [] })),
      ]);
      setServices(sRes.data);
      setVolumes(vRes.data);
      setWorkloads(Array.isArray(wRes.data) ? wRes.data : []);
      setError('');
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleExpand = async (serviceId) => {
    if (expandedService === serviceId) {
      setExpandedService(null);
      return;
    }
    setExpandedService(serviceId);
    if (!serviceContainers[serviceId]) {
      try {
        const res = await swarmApi.getServiceContainers(serviceId);
        setServiceContainers(prev => ({ ...prev, [serviceId]: res.data }));
      } catch {
        setServiceContainers(prev => ({ ...prev, [serviceId]: [] }));
      }
    }
  };

  const viewServiceLogs = async (id, name) => {
    setLogsDialog({ open: true, id, name, logs: '', loading: true });
    try {
      const res = await swarmApi.getServiceLogs(id);
      setLogsDialog(prev => ({ ...prev, logs: res.data.logs, loading: false }));
    } catch {
      setLogsDialog(prev => ({ ...prev, logs: 'Failed to fetch logs', loading: false }));
    }
  };

  const viewContainerLogs = async (id, name) => {
    setLogsDialog({ open: true, id, name, logs: '', loading: true });
    try {
      const res = await swarmApi.getContainerLogs(id);
      setLogsDialog(prev => ({ ...prev, logs: res.data.logs, loading: false }));
    } catch {
      setLogsDialog(prev => ({ ...prev, logs: 'Failed to fetch logs', loading: false }));
    }
  };

  const inspectService = async (id) => {
    try {
      const res = await swarmApi.inspectService(id);
      setDetailDialog({ open: true, data: res.data, title: `Service: ${res.data.name || id}` });
    } catch {
      setError('Failed to inspect service');
    }
  };

  const scaleService = async (id, currentReplicas) => {
    const count = prompt('Scale to how many replicas?', currentReplicas || '1');
    if (count !== null && !isNaN(count)) {
      try {
        await swarmApi.scaleService(id, parseInt(count));
        fetchData();
      } catch { setError('Failed to scale service'); }
    }
  };

  const restartService = async (id) => {
    try {
      await swarmApi.restartService(id);
      fetchData();
    } catch { setError('Failed to restart service'); }
  };

  const openOrchestrator = (service) => {
    setOrchestratorDialog({
      open: true,
      serviceId: service.id,
      constraints: service.constraints ? service.constraints.join(', ') : '',
      memoryLimit: service.memory_limit || ''
    });
  };

  const applyOrchestration = async () => {
    try {
      const constraintsArr = orchestratorDialog.constraints.split(',').map(s => s.trim()).filter(Boolean);
      const data = {};
      if (constraintsArr.length > 0) data.constraints = constraintsArr;
      if (orchestratorDialog.memoryLimit) data.memory_limit = orchestratorDialog.memoryLimit;

      await swarmApi.updateService(orchestratorDialog.serviceId, data);
      setOrchestratorDialog({ open: false, serviceId: '', constraints: '', memoryLimit: '' });
      fetchData();
    } catch {
      setError('Failed to apply orchestration config');
    }
  };

  const removeService = async (id, name) => {
    if (window.confirm(`Remove service "${name}"? This will stop all its containers.`)) {
      try {
        await swarmApi.removeService(id);
        fetchData();
      } catch { setError('Failed to remove service'); }
    }
  };

  const createVolume = async () => {
    if (!volumeDialog.name.trim()) return;
    try {
      await swarmApi.createVolume(volumeDialog.name, volumeDialog.driver);
      setVolumeDialog({ open: false, name: '', driver: 'local' });
      fetchData();
    } catch {
      setError('Failed to create volume');
    }
  };

  const deployWorkload = async () => {
    if (!deployDialog.name.trim() || !deployDialog.image.trim()) {
      setError('Name and image are required');
      return;
    }
    setDeployDialog(d => ({ ...d, submitting: true }));
    try {
      const env = deployDialog.envText
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
      const ports = [];
      if (deployDialog.targetPort) {
        ports.push({
          targetPort: parseInt(deployDialog.targetPort, 10),
          publishedPort: parseInt(deployDialog.publishedPort || deployDialog.targetPort, 10),
          protocol: 'tcp',
        });
      }
      const body = {
        name: deployDialog.name.trim(),
        image: deployDialog.image.trim(),
        replicas: parseInt(deployDialog.replicas, 10) || 1,
        deployNow: true,
        reconcileEnabled: true,
      };
      if (env.length) body.env = env;
      if (ports.length) body.ports = ports;
      if (deployDialog.network.trim()) body.networks = [deployDialog.network.trim()];

      await swarmApi.createWorkload(body);
      setDeployDialog({ open: false, ...emptyDeployForm(), submitting: false });
      setSuccess(`Workload "${body.name}" deployed`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deploy workload');
      setDeployDialog(d => ({ ...d, submitting: false }));
    }
  };

  const redeployWorkload = async (id) => {
    try {
      await swarmApi.deployWorkload(id);
      setSuccess('Workload redeployed');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Redeploy failed');
    }
  };

  const deleteWorkload = async (id, name) => {
    if (!window.confirm(`Delete workload "${name}" and remove its Swarm service?`)) return;
    try {
      await swarmApi.deleteWorkload(id, true);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const openHpa = (w) => {
    setHpaDialog({
      open: true,
      workload: {
        id: w.id,
        name: w.name,
        autoscalingEnabled: !!w.autoscalingEnabled,
        minReplicas: w.minReplicas ?? 1,
        maxReplicas: w.maxReplicas ?? 10,
        targetCpuPercent: w.targetCpuPercent ?? 70,
        targetMemoryPercent: w.targetMemoryPercent ?? 80,
        scaleUpCooldownSeconds: w.scaleUpCooldownSeconds ?? 60,
        scaleDownCooldownSeconds: w.scaleDownCooldownSeconds ?? 180,
      },
      submitting: false,
    });
  };

  const saveHpa = async () => {
    const w = hpaDialog.workload;
    if (!w) return;
    setHpaDialog(d => ({ ...d, submitting: true }));
    try {
      await swarmApi.updateWorkload(w.id, {
        autoscalingEnabled: w.autoscalingEnabled,
        minReplicas: parseInt(w.minReplicas, 10) || 1,
        maxReplicas: parseInt(w.maxReplicas, 10) || 10,
        targetCpuPercent: parseFloat(w.targetCpuPercent) || null,
        targetMemoryPercent: parseFloat(w.targetMemoryPercent) || null,
        scaleUpCooldownSeconds: parseInt(w.scaleUpCooldownSeconds, 10) || 60,
        scaleDownCooldownSeconds: parseInt(w.scaleDownCooldownSeconds, 10) || 180,
        deployNow: false,
      });
      setHpaDialog({ open: false, workload: null, submitting: false });
      setSuccess(`Autoscaling updated for ${w.name}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update autoscaling');
      setHpaDialog(d => ({ ...d, submitting: false }));
    }
  };

  const explainService = async (id, name) => {
    setExplainDialog({ open: true, title: name || id, text: '', loading: true });
    try {
      const res = await swarmApi.explainService(id);
      const d = res.data;
      const metrics = d.metrics
        ? `\nMetrics: CPU ${d.metrics.cpuPercent ?? 'n/a'}% · MEM ${d.metrics.memoryPercent ?? 'n/a'}%`
        : '';
      setExplainDialog({
        open: true,
        title: d.serviceName || name,
        text: `${d.explanation || ''}${metrics}\n\nStatus: ${d.status} (${d.runningReplicas}/${d.desiredReplicas} replicas)`,
        loading: false,
      });
    } catch (err) {
      setExplainDialog({
        open: true,
        title: name || id,
        text: err.response?.data?.message || 'Explain failed',
        loading: false,
      });
    }
  };

  const removeVolume = async (name) => {
    if (window.confirm(`Remove volume "${name}"? This will delete all data in this volume.`)) {
      try {
        await swarmApi.removeVolume(name, true);
        fetchData();
      } catch { setError('Failed to remove volume'); }
    }
  };

  const statusColor = (status) => {
    if (status === 'running') return 'success';
    if (status === 'degraded') return 'warning';
    return 'error';
  };

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Services & Volumes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDeployDialog({ open: true, ...emptyDeployForm(), submitting: false })}>
          Deploy Workload
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Services (${services.length})`} icon={<StorageIcon />} iconPosition="start" />
        <Tab label={`Workloads (${workloads.length})`} icon={<AddIcon />} iconPosition="start" />
        <Tab label={`Volumes (${volumes.length})`} icon={<StorageIcon />} iconPosition="start" />
      </Tabs>

      {/* ─── Services Tab ─── */}
      {tab === 0 && (
        <Box>
          {services.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>No services found. Deploy a workload from the UI or a compose stack to get started.</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDeployDialog({ open: true, ...emptyDeployForm(), submitting: false })}>
                Deploy Workload
              </Button>
            </Paper>
          ) : (
            services.map((s) => (
              <Paper key={s.id} sx={{ mb: 2, overflow: 'hidden' }}>
                {/* Service Header Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, cursor: 'pointer' }} onClick={() => toggleExpand(s.id)}>
                  <IconButton size="small" sx={{ mr: 1 }}>
                    {expandedService === s.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="span">{s.name}</Typography>
                    {s.updateStatus && s.updateStatus !== 'completed' && (
                      <Chip label={`Updating: ${s.updateStatus}`} size="small" color="info" sx={{ ml: 1 }} />
                    )}
                    <Chip label={s.status} size="small" color={statusColor(s.status)} sx={{ ml: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {s.image} &bull; Replicas: {s.runningReplicas}/{s.desiredReplicas}
                      {s.ports?.length > 0 && ` • Ports: ${s.ports.map(p => `${p.publishedPort}:${p.targetPort}`).join(', ')}`}
                    </Typography>
                  </Box>
                  <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Orchestrate"><IconButton size="small" onClick={() => openOrchestrator(s)}><TuneIcon color="primary" /></IconButton></Tooltip>
                    <Tooltip title="Inspect"><IconButton size="small" onClick={() => inspectService(s.id)}><InfoIcon /></IconButton></Tooltip>
                    <Tooltip title="Logs"><IconButton size="small" onClick={() => viewServiceLogs(s.id, s.name)}><ArticleIcon /></IconButton></Tooltip>
                    <Tooltip title="Scale"><IconButton size="small" onClick={() => scaleService(s.id, s.desiredReplicas)}><ScaleIcon /></IconButton></Tooltip>
                    <Tooltip title="Restart"><IconButton size="small" onClick={() => restartService(s.id)}><RestartAltIcon /></IconButton></Tooltip>
                    <Tooltip title="AI Explain">
                      <IconButton size="small" color="primary" onClick={() => explainService(s.id, s.name)}>
                        <SmartToyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="AI Evaluate (recommend action)">
                      <IconButton size="small" color="secondary" onClick={async () => {
                        try {
                          const res = await swarmApi.triggerAiEvaluation(s.id);
                          alert(res.data.message);
                        } catch (err) {
                          alert('Evaluation failed: ' + (err.response?.data?.message || err.message));
                        }
                      }}>
                        <TuneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(s.status === 'degraded' || s.status === 'stopped') && (
                      <Tooltip title="Rollback to previous version">
                        <IconButton size="small" color="warning" onClick={async () => {
                          if (window.confirm(`Rollback ${s.name} to previous image version?`)) {
                            try { await swarmApi.rollbackService(s.id); fetchData(); }
                            catch (err) { setError('Rollback failed: ' + (err.response?.data?.message || err.message)); }
                          }
                        }}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => removeService(s.id, s.name)}><DeleteIcon /></IconButton></Tooltip>
                  </Box>
                </Box>

                {/* Mounts Summary */}
                {s.mounts?.length > 0 && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Volumes: {s.mounts.filter(m => m.type === 'volume').map(m => m.source).join(', ') || 'none'}
                      {s.mounts.filter(m => m.type === 'bind').length > 0 && ` | Binds: ${s.mounts.filter(m => m.type === 'bind').map(m => m.source).join(', ')}`}
                    </Typography>
                  </Box>
                )}

                {/* Expanded: Show containers belonging to this service */}
                <Collapse in={expandedService === s.id}>
                  <Divider />
                  <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" gutterBottom>Containers (Tasks)</Typography>
                    {serviceContainers[s.id]?.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>State</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Image</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {serviceContainers[s.id].map((c) => (
                              <TableRow key={c.id}>
                                <TableCell>{c.name || c.id?.substring(0, 12)}</TableCell>
                                <TableCell><Chip label={c.state} size="small" color={c.state === 'running' ? 'success' : 'error'} /></TableCell>
                                <TableCell>{c.status}</TableCell>
                                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.image}</TableCell>
                                <TableCell>
                                  <Tooltip title="Container Logs">
                                    <IconButton size="small" onClick={() => viewContainerLogs(c.id, c.name)}><ArticleIcon fontSize="small" /></IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {serviceContainers[s.id] ? 'No containers found for this service' : 'Loading...'}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            ))
          )}
        </Box>
      )}

      {/* ─── Workloads Tab (desired-state) ─── */}
      {tab === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Platform-managed desired state. The reconciler keeps Swarm services in sync with these definitions.
          </Typography>
          {workloads.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No workloads yet. Deploy an image to create a managed Swarm service.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDeployDialog({ open: true, ...emptyDeployForm(), submitting: false })}>
                Deploy Workload
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>Replicas</TableCell>
                    <TableCell>HPA</TableCell>
                    <TableCell>Metrics</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workloads.map((w) => (
                    <TableRow key={w.id} hover>
                      <TableCell>{w.name}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.image}</TableCell>
                      <TableCell>
                        {w.runningReplicas != null ? `${w.runningReplicas}/` : ''}{w.replicas}
                        {w.autoscalingEnabled && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            min {w.minReplicas} / max {w.maxReplicas}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={w.autoscalingEnabled ? 'on' : 'off'}
                          color={w.autoscalingEnabled ? 'success' : 'default'}
                          onClick={() => openHpa(w)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        {w.lastMetrics ? (
                          <Typography variant="caption">
                            CPU {w.lastMetrics.cpuPercent ?? '—'}% · MEM {w.lastMetrics.memoryPercent ?? '—'}%
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={w.status} size="small" color={statusColor(w.status === 'running' ? 'running' : w.status === 'degraded' ? 'degraded' : 'stopped')} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Autoscaling policy">
                          <IconButton size="small" onClick={() => openHpa(w)}><TuneIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Redeploy / reconcile">
                          <IconButton size="small" onClick={() => redeployWorkload(w.id)}><RestartAltIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete workload">
                          <IconButton size="small" color="error" onClick={() => deleteWorkload(w.id, w.name)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {workloads.some(w => w.lastError) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Some workloads have errors: {workloads.filter(w => w.lastError).map(w => `${w.name}: ${w.lastError}`).join('; ')}
            </Alert>
          )}
        </Box>
      )}

      {/* ─── Volumes Tab ─── */}
      {tab === 2 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setVolumeDialog({ open: true, name: '', driver: 'local' })}>
              Create Volume
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Used By Services</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {volumes.map((v) => (
                  <TableRow key={v.name} hover>
                    <TableCell>{v.name}</TableCell>
                    <TableCell>{v.driver}</TableCell>
                    <TableCell>
                      {v.usedByServices?.length > 0
                        ? v.usedByServices.map((svc, i) => <Chip key={i} label={svc} size="small" sx={{ mr: 0.5 }} />)
                        : <Typography variant="body2" color="text.secondary">Unused</Typography>
                      }
                    </TableCell>
                    <TableCell>{v.createdAt ? new Date(v.createdAt).toLocaleString() : '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Remove Volume">
                        <IconButton size="small" color="error" onClick={() => removeVolume(v.name)}
                          disabled={v.usedByServices?.length > 0}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {volumes.length === 0 && (
                  <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No volumes found</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Logs Dialog */}
      <Dialog open={logsDialog.open} onClose={() => setLogsDialog(p => ({ ...p, open: false }))} maxWidth="md" fullWidth>
        <DialogTitle>Logs: {logsDialog.name}</DialogTitle>
        <DialogContent>
          {logsDialog.loading ? <CircularProgress /> : (
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {logsDialog.logs || 'No logs available'}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setLogsDialog(p => ({ ...p, open: false }))}>Close</Button></DialogActions>
      </Dialog>

      {/* Detail/Inspect Dialog */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, data: null, title: '' })} maxWidth="md" fullWidth>
        <DialogTitle>{detailDialog.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 500, overflow: 'auto' }}>
            {JSON.stringify(detailDialog.data, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailDialog({ open: false, data: null, title: '' })}>Close</Button></DialogActions>
      </Dialog>

      {/* Create Volume Dialog */}
      <Dialog open={volumeDialog.open} onClose={() => setVolumeDialog({ open: false, name: '', driver: 'local' })}>
        <DialogTitle>Create Volume</DialogTitle>
        <DialogContent>
          <TextField label="Volume Name" fullWidth margin="normal" value={volumeDialog.name}
            onChange={e => setVolumeDialog(p => ({ ...p, name: e.target.value }))} />
          <TextField label="Driver" fullWidth margin="normal" value={volumeDialog.driver}
            onChange={e => setVolumeDialog(p => ({ ...p, driver: e.target.value }))} helperText="Default: local" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVolumeDialog({ open: false, name: '', driver: 'local' })}>Cancel</Button>
          <Button variant="contained" onClick={createVolume} disabled={!volumeDialog.name.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Orchestrator Dialog */}
      <Dialog open={orchestratorDialog.open} onClose={() => setOrchestratorDialog(p => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Orchestrate Service Placement & Limits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage swarm constraints to pin services to specific workers, and limit container execution thresholds.
          </Typography>
          <TextField 
            label="Placement Constraints (comma-separated)" 
            fullWidth margin="normal" 
            placeholder="e.g. node.labels.role == trading"
            value={orchestratorDialog.constraints}
            onChange={e => setOrchestratorDialog(p => ({ ...p, constraints: e.target.value }))} 
          />
          <TextField 
            label="Memory Limit (bytes)" 
            fullWidth margin="normal" 
            placeholder="e.g. 1073741824 for 1GB"
            value={orchestratorDialog.memoryLimit}
            onChange={e => setOrchestratorDialog(p => ({ ...p, memoryLimit: e.target.value }))} 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrchestratorDialog(p => ({ ...p, open: false }))}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={applyOrchestration}>Apply Updates</Button>
        </DialogActions>
      </Dialog>

      {/* HPA / Autoscaling Dialog */}
      <Dialog open={hpaDialog.open} onClose={() => !hpaDialog.submitting && setHpaDialog({ open: false, workload: null, submitting: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Autoscaling: {hpaDialog.workload?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Requires Prometheus (set Prometheus__BaseUrl). Scales within min/max using CPU/memory targets.
          </Typography>
          {hpaDialog.workload && (
            <>
              <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                <Button
                  size="small"
                  variant={hpaDialog.workload.autoscalingEnabled ? 'contained' : 'outlined'}
                  color="success"
                  onClick={() => setHpaDialog(d => ({ ...d, workload: { ...d.workload, autoscalingEnabled: true } }))}
                >
                  Enabled
                </Button>
                <Button
                  size="small"
                  variant={!hpaDialog.workload.autoscalingEnabled ? 'contained' : 'outlined'}
                  onClick={() => setHpaDialog(d => ({ ...d, workload: { ...d.workload, autoscalingEnabled: false } }))}
                >
                  Disabled
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField label="Min replicas" type="number" fullWidth margin="normal"
                  value={hpaDialog.workload.minReplicas}
                  onChange={e => setHpaDialog(d => ({ ...d, workload: { ...d.workload, minReplicas: e.target.value } }))} />
                <TextField label="Max replicas" type="number" fullWidth margin="normal"
                  value={hpaDialog.workload.maxReplicas}
                  onChange={e => setHpaDialog(d => ({ ...d, workload: { ...d.workload, maxReplicas: e.target.value } }))} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField label="Target CPU %" type="number" fullWidth margin="normal"
                  value={hpaDialog.workload.targetCpuPercent}
                  onChange={e => setHpaDialog(d => ({ ...d, workload: { ...d.workload, targetCpuPercent: e.target.value } }))} />
                <TextField label="Target Memory %" type="number" fullWidth margin="normal"
                  value={hpaDialog.workload.targetMemoryPercent}
                  onChange={e => setHpaDialog(d => ({ ...d, workload: { ...d.workload, targetMemoryPercent: e.target.value } }))} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField label="Scale-up cooldown (s)" type="number" fullWidth margin="normal"
                  value={hpaDialog.workload.scaleUpCooldownSeconds}
                  onChange={e => setHpaDialog(d => ({ ...d, workload: { ...d.workload, scaleUpCooldownSeconds: e.target.value } }))} />
                <TextField label="Scale-down cooldown (s)" type="number" fullWidth margin="normal"
                  value={hpaDialog.workload.scaleDownCooldownSeconds}
                  onChange={e => setHpaDialog(d => ({ ...d, workload: { ...d.workload, scaleDownCooldownSeconds: e.target.value } }))} />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={hpaDialog.submitting} onClick={() => setHpaDialog({ open: false, workload: null, submitting: false })}>Cancel</Button>
          <Button variant="contained" onClick={saveHpa} disabled={hpaDialog.submitting}>
            {hpaDialog.submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Explain Dialog */}
      <Dialog open={explainDialog.open} onClose={() => setExplainDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>AI Explain: {explainDialog.title}</DialogTitle>
        <DialogContent>
          {explainDialog.loading ? <CircularProgress /> : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{explainDialog.text}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplainDialog(d => ({ ...d, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Deploy Workload Dialog */}
      <Dialog open={deployDialog.open} onClose={() => !deployDialog.submitting && setDeployDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Deploy Workload</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Creates a desired-state workload and deploys it as a Docker Swarm service.
          </Typography>
          <TextField
            label="Service name" fullWidth margin="normal" required
            value={deployDialog.name}
            onChange={e => setDeployDialog(d => ({ ...d, name: e.target.value }))}
            helperText="Must be unique in the swarm"
          />
          <TextField
            label="Image" fullWidth margin="normal" required
            placeholder="e.g. nginx:alpine or traefik/whoami:latest"
            value={deployDialog.image}
            onChange={e => setDeployDialog(d => ({ ...d, image: e.target.value }))}
          />
          <TextField
            label="Replicas" type="number" fullWidth margin="normal"
            inputProps={{ min: 0, max: 100 }}
            value={deployDialog.replicas}
            onChange={e => setDeployDialog(d => ({ ...d, replicas: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Published port" type="number" fullWidth margin="normal"
              placeholder="host"
              value={deployDialog.publishedPort}
              onChange={e => setDeployDialog(d => ({ ...d, publishedPort: e.target.value }))}
            />
            <TextField
              label="Target port" type="number" fullWidth margin="normal"
              placeholder="container"
              value={deployDialog.targetPort}
              onChange={e => setDeployDialog(d => ({ ...d, targetPort: e.target.value }))}
            />
          </Box>
          <TextField
            label="Overlay network (optional)" fullWidth margin="normal"
            placeholder="e.g. swarm-net"
            value={deployDialog.network}
            onChange={e => setDeployDialog(d => ({ ...d, network: e.target.value }))}
          />
          <TextField
            label="Environment (one KEY=VALUE per line)" fullWidth margin="normal" multiline minRows={3}
            value={deployDialog.envText}
            onChange={e => setDeployDialog(d => ({ ...d, envText: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={deployDialog.submitting} onClick={() => setDeployDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button
            variant="contained"
            onClick={deployWorkload}
            disabled={deployDialog.submitting || !deployDialog.name.trim() || !deployDialog.image.trim()}
          >
            {deployDialog.submitting ? 'Deploying…' : 'Deploy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Services;
